import React, { useEffect, useState, useContext } from 'react';
import {
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getOrders, getOrderCount } from '../services/orderService';
import { getCustomers } from '../services/customerService';
import { getSuppliers } from '../services/supplierService';
import { getProductsByOrgId } from '../services/productService';
import { getInventoryItemsByOrgId, getOrganizationMovements } from '../services/inventoryService';
import CurrencyFormatter from '../utils/CurrencyFormatter';
import formatDateToStandard from '../utils/DateFormatter';
import { useNavigate } from 'react-router-dom';
import { OrgContext } from '../contexts/OrgContext';
import OnboardingGuide from '../components/OnboardingGuide';
// Removed unused color imports

const CustomTooltip = ({ active, payload, label, type }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
  <div className="chart-tooltip">
        {type === 'inventory' ? (
          <>
            <p><strong>Product:</strong> {data.name}</p>
            <p><strong>Quantity:</strong> {data.value}</p>
            <p><strong>Category:</strong> {data.category}</p>
          </>
        ) : type === 'inventory-levels' ? (
          <>
            <p><strong>{data.name}</strong></p>
            <p><strong>Count:</strong> {data.value}</p>
    <p><strong>Products:</strong></p>
    <ul>
              {data.products.map((product, idx) => (
                <li key={idx}>{product.name} ({product.quantity})</li>
              ))}
            </ul>
          </>
        ) : type === 'orders' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(data.date)}</p>
            <p><strong>Orders:</strong> {data.count}</p>
            <p><strong>Total Amount:</strong> <CurrencyFormatter value={data.amount} /></p>
          </>
        ) : type === 'orders-timeline' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
            <p><strong>Sales Orders:</strong> {payload[0].payload.salesCount} orders</p>
            <p><strong>Sales Amount:</strong> <CurrencyFormatter value={payload[0].payload.salesAmount} /></p>
            <p><strong>Purchase Orders:</strong> {payload[0].payload.purchaseCount} orders</p>
            <p><strong>Purchase Amount:</strong> <CurrencyFormatter value={payload[0].payload.purchaseAmount} /></p>
            <p><strong>Total Orders:</strong> {payload[0].payload.salesCount + payload[0].payload.purchaseCount}</p>
            {/* <p><strong>Total Amount:</strong> <CurrencyFormatter value={payload[0].payload.salesAmount + payload[0].payload.purchaseAmount} /></p> */}
          </>
        ) : type === 'inventory-movements' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
            {payload.map((entry, index) => (
      <p key={index} style={{ color: entry.color }}>
                <strong>{entry.name}:</strong> {entry.value} units
              </p>
            ))}
          </>
        ) : (
          <>
            <p><strong>{label}</strong></p>
            <p><strong>Value:</strong> {data.value}</p>
          </>
        )}
      </div>
    );
  }
  return null;
};

// Custom X-axis tick for Inventory by Product with interactive hover tooltip (mirrors Reports implementation style)
const ProductNameTick = ({ x, y, payload }) => {
  const [hover, setHover] = React.useState(false);
  const full = payload?.value || '';
  const MAX_CHARS = 10;
  const needsTruncate = full.length > MAX_CHARS;
  const truncated = needsTruncate ? full.slice(0, MAX_CHARS) + '…' : full;
  // Estimate text width; fallback multiplier 7 per char
  const estWidth = Math.min(Math.max(full.length * 7, 40), 180);
  const tooltipY = y + 26; // below tick label
  return (
    <g transform={`translate(${x},${y})`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ cursor: needsTruncate ? 'pointer' : 'default' }}>
      <text className="chart-tick-text" dy={16} textAnchor="middle" style={{ fontSize: 12 }}>
        {truncated}
      </text>
      {hover && needsTruncate && (
        <g>
          <rect
            className="chart-svg-tooltip-bg"
            x={-estWidth / 2}
            y={tooltipY}
            width={estWidth}
            height={24}
            rx={4}
          />
          <text
            className="chart-tick-text"
            x={0}
            y={tooltipY + 16}
            textAnchor="middle"
            style={{ fontSize: 11, fontWeight: 500, pointerEvents: 'none' }}
          >
            {full}
          </text>
        </g>
      )}
    </g>
  );
};

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  // Removed inventory state (not used directly in UI after refactor)
  // const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [products, setProducts] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  // Removed totalProducts card per requirement (5 cards)
  // const [totalProducts, setTotalProducts] = useState(0);
  const [inventoryLevels, setInventoryLevels] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [inventoryMovements, setInventoryMovements] = useState([]);
  const [groupedMovements, setGroupedMovements] = useState([]); // Initialize as empty array instead of empty object
  // Onboarding control state computed before main content displays
  const [onboardingComputed, setOnboardingComputed] = useState(false);
  const [onboardingStatuses, setOnboardingStatuses] = useState({
    ADD_PRODUCT: false,
    ADD_CUSTOMER_OR_SUPPLIER: false,
    ADD_INVENTORY: false,
    CREATE_SALES_ORDER: false,
    CREATE_PURCHASE_ORDER: false,
  });
  const [hideOnboarding, setHideOnboarding] = useState(false);
  const [onboardingDefaultCollapsed, setOnboardingDefaultCollapsed] = useState(true);

  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext);

  const calculateTotals = (orders) => {
    let totalSales = 0;
    let totalPurchases = 0;

    orders.forEach(order => {
      if (order.order_type === 'Sale') {
        totalSales += parseFloat(order.amount);
      } else if (order.order_type === 'Purchase') {
        totalPurchases += parseFloat(order.amount);
      }
    });

    return { totalSales, totalPurchases };
  };

  useEffect(() => {
    if (!orgId) return;
    // Pre-compute onboarding completion first so initial render respects it
    const prefetchOnboarding = async () => {
      try {
  const [productsResp, customersResp, suppliersResp, inventoryResp, ordersResp] = await Promise.all([
          getProductsByOrgId(orgId, { limit: 1, page: 1 }),
          getCustomers(orgId, { limit: 1, page: 1 }),
          getSuppliers(orgId, { limit: 1, page: 1 }),
          getInventoryItemsByOrgId(orgId, { limit: 1, page: 1 }),
          getOrders({ limit: 50, page: 1 }),
        ]);
        const arr = (r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []);
        const products = arr(productsResp);
        const customers = arr(customersResp);
        const suppliers = arr(suppliersResp);
        const inventory = arr(inventoryResp);
        const orders = arr(ordersResp);
        const statuses = {
          ADD_PRODUCT: products.length > 0,
          ADD_CUSTOMER_OR_SUPPLIER: (customers.length > 0) || (suppliers.length > 0),
          ADD_INVENTORY: inventory.length > 0 && inventory.some(i => (i.quantity_on_hand ?? i.quantity ?? 0) > 0),
          CREATE_SALES_ORDER: orders.some(o => (o.order_type || o.type) === 'Sale' || (o.order_type || o.type) === 'SALE'),
          CREATE_PURCHASE_ORDER: orders.some(o => (o.order_type || o.type) === 'Purchase' || (o.order_type || o.type) === 'PURCHASE'),
        };
        setOnboardingStatuses(statuses);
  const completed = Object.values(statuses).filter(Boolean).length;
  const total = Object.keys(statuses).length;
  const allDone = completed === total;
  setHideOnboarding(allDone);
  // Collapse by default only if all done; otherwise expand by default
  setOnboardingDefaultCollapsed(allDone);
      } catch (e) {
        // If this fails, keep default collapsed true and show guide (safe default)
        console.warn('Onboarding prefetch failed:', e);
        setHideOnboarding(false);
        setOnboardingDefaultCollapsed(true);
      } finally {
        setOnboardingComputed(true);
      }
    };

    prefetchOnboarding();

    setLoading(true);
    
    const fetchAllData = async () => {
      try {
        // Fetch first page to get total counts
        const [
          ordersFirstPage,
          inventoryFirstPage, 
          customersFirstPage,
          suppliersFirstPage,
          productsResponse,
          movementsData
        ] = await Promise.all([
          getOrders({ limit: 0, page: 1 }),
          getInventoryItemsByOrgId(orgId, { limit: 0, page: 1 }),
          getCustomers(orgId, { limit: 0, page: 1 }),
          getSuppliers(orgId, { limit: 0, page: 1 }),
          getProductsByOrgId(orgId, { limit: 0, page: 1 }),
          getOrganizationMovements(orgId, 20)
        ]);

        console.log('Dashboard API responses:', {
          orders: ordersFirstPage,
          inventory: inventoryFirstPage,
          customers: customersFirstPage,
          suppliers: suppliersFirstPage,
          products: productsResponse
        });

        // Extract data from responses, handling both paginated and non-paginated formats
  const orders = ordersFirstPage?.data || ordersFirstPage || [];
        const inventory = inventoryFirstPage?.data || inventoryFirstPage || [];
        const customers = customersFirstPage?.data || customersFirstPage || [];
        const suppliers = suppliersFirstPage?.data || suppliersFirstPage || [];
  // const products = productsResponse?.data || productsResponse || [];
  // totalProductsCount removed (not displayed currently)
        const { totalSales, totalPurchases } = calculateTotals(orders);

        setOrders({ totalSales, totalPurchases });
  // inventory state removed
        setCustomers(customers.length); // Store just the count
        setSuppliers(suppliers.length); // Store just the count
  // products & totalProducts states removed
        // Fetch authoritative order count from backend to avoid pagination/format mismatches
        try {
          const count = await getOrderCount();
          setTotalOrders(count);
        } catch {
          setTotalOrders(orders.length);
        }

        // Enhanced order history processing
        const ordersByDate = orders.reduce((acc, order) => {
          const date = order.order_date; // Use order_date directly from API
          if (!acc[date]) {
            acc[date] = { 
              date,
              salesCount: 0,
              purchaseCount: 0,
              salesAmount: 0,
              purchaseAmount: 0
            };
          }
          
          if (order.order_type === 'Sale') {
            acc[date].salesCount++;
            acc[date].salesAmount += parseFloat(order.amount || 0);
          } else if (order.order_type === 'Purchase') {
            acc[date].purchaseCount++;
            acc[date].purchaseAmount += parseFloat(order.amount || 0);
          }
          
          return acc;
        }, {});

  // Build order history entries and keep a DESC array for display
  const historyEntries = Object.values(ordersByDate);
  const ascHistory = historyEntries.sort((a, b) => new Date(a.date) - new Date(b.date));
  const descHistory = [...ascHistory].reverse();
  setOrderHistory(descHistory);

        // Enhanced inventory levels with product details
        const inventoryLevels = [
          {
            name: 'Low Stock',
            value: inventory.filter(item => item.quantity_on_hand < item.minimum_stock_level).length,
            products: inventory
              .filter(item => item.quantity_on_hand < item.minimum_stock_level)
              .map(item => ({
                name: item.product.product_name,
                quantity: `${item.quantity_on_hand} ${item.product.unit_of_measure}`
              }))
          },
          {
            name: 'Sufficient Stock',
            value: inventory.filter(
              item => 
                item.quantity_on_hand >= item.minimum_stock_level && 
                !item.excess_inventory
            ).length,
            products: inventory
              .filter(item => 
                item.quantity_on_hand >= item.minimum_stock_level && 
                !item.excess_inventory
              )
              .map(item => ({
                name: item.product.product_name,
                quantity: `${item.quantity_on_hand} ${item.product.unit_of_measure}`
              }))
          },
          {
            name: 'Excess Stock',
            value: inventory.filter(item => item.excess_inventory).length,
            products: inventory
              .filter(item => item.excess_inventory)
              .map(item => ({
                name: item.product.product_name,
                quantity: `${item.quantity_on_hand} ${item.product.unit_of_measure}`
              }))
          }
        ];
        setInventoryLevels(inventoryLevels);

        // Enhanced inventory data with product details
        const inventoryData = inventory.map(item => ({
          name: item.product.product_name,
          value: item.quantity_on_hand, // Keep raw quantity for charts
          displayValue: `${item.quantity_on_hand} ${item.product.unit_of_measure}`, // Add displayValue for tables
          category: item.product.category,
          sub_category: item.product.sub_category
        }));
        setInventoryData(inventoryData);

        // Set inventory movements data
        if (movementsData.timelineData) {
          const processedTimelineData = movementsData.timelineData.map(entry => ({
            ...entry,
            // Convert string date to actual Date object for proper timeline display
            date_of_movement: new Date(entry.date_of_movement).getTime()
          }));

          setInventoryMovements(processedTimelineData);
          setGroupedMovements(movementsData.products);
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, [navigate, orgId]);

  const COLORS = ['#FF8042', '#00C49F', '#FFBB28'];
  // Feed ascending data to the timeline X-axis, while state keeps DESC for display elsewhere
  const orderHistoryAsc = React.useMemo(
    () => [...orderHistory].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [orderHistory]
  );

  return (
    <Paper sx={{ p: 3 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h4" gutterBottom>Dashboard</Typography>
          {/* Getting Started Guide - only render after onboardingComputed so it respects visibility/collapsed */}
      {onboardingComputed && !hideOnboarding && (
            <OnboardingGuide
        defaultCollapsed={onboardingDefaultCollapsed}
              hidden={hideOnboarding}
              initialStatuses={onboardingStatuses}
            />
          )}
          {/* Shortcuts - eye-catching quick actions */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: 1, opacity: 0.85, display: 'block', mb: 1 }}
            >
              Quick actions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {/* Quick Sale */}
              <Paper
                className="surface-card"
                onClick={() => navigate(`/${orgId}/orders/quick-sale`)}
                sx={{
                  p: 2,
                  pr: 2.25,
                  borderRadius: 2.5,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  minWidth: { xs: '100%', sm: 260 },
                  flex: { xs: '1 1 100%', sm: '0 0 auto' },
                  color: 'common.white',
                  backgroundImage: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 14px 28px rgba(16,185,129,0.35)',
                  transition: 'transform .2s ease, box-shadow .2s ease, filter .2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(16,185,129,0.45)',
                    filter: 'brightness(1.03)'
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    right: -24,
                    top: -24,
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    background: 'radial-gradient(closest-side, rgba(255,255,255,0.22), rgba(255,255,255,0))',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.35)',
                      backdropFilter: 'blur(3px)'
                    }}
                  >
                    <PointOfSaleIcon fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                      Quick Sale
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.95 }}>
                      Scan or search and checkout fast
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ opacity: 0.9 }} />
                </Box>
              </Paper>

              {/* Quick Purchase */}
              <Paper
                className="surface-card"
                onClick={() => navigate(`/${orgId}/orders/quick-purchase`)}
                sx={{
                  p: 2,
                  pr: 2.25,
                  borderRadius: 2.5,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  minWidth: { xs: '100%', sm: 260 },
                  flex: { xs: '1 1 100%', sm: '0 0 auto' },
                  color: 'common.white',
                  backgroundImage: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                  boxShadow: '0 14px 28px rgba(59,130,246,0.35)',
                  transition: 'transform .2s ease, box-shadow .2s ease, filter .2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(59,130,246,0.45)',
                    filter: 'brightness(1.03)'
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    right: -24,
                    top: -24,
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    background: 'radial-gradient(closest-side, rgba(255,255,255,0.22), rgba(255,255,255,0))',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.35)',
                      backdropFilter: 'blur(3px)'
                    }}
                  >
                    <ShoppingCartCheckoutIcon fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                      Quick Purchase
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.95 }}>
                      Add stock from suppliers in seconds
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ opacity: 0.9 }} />
                </Box>
              </Paper>

              {/* Add Inventory */}
              <Paper
                className="surface-card"
                onClick={() => navigate(`/${orgId}/inventory/add`)}
                sx={{
                  p: 2,
                  pr: 2.25,
                  borderRadius: 2.5,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  minWidth: { xs: '100%', sm: 260 },
                  flex: { xs: '1 1 100%', sm: '0 0 auto' },
                  color: 'common.white',
                  backgroundImage: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                  boxShadow: '0 14px 28px rgba(239,68,68,0.35)',
                  transition: 'transform .2s ease, box-shadow .2s ease, filter .2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(239,68,68,0.45)',
                    filter: 'brightness(1.03)'
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    right: -24,
                    top: -24,
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    background: 'radial-gradient(closest-side, rgba(255,255,255,0.22), rgba(255,255,255,0))',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.35)',
                      backdropFilter: 'blur(3px)'
                    }}
                  >
                    <Inventory2Icon fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                      Add Inventory
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.95 }}>
                      Update stock quickly and accurately
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ opacity: 0.9 }} />
                </Box>
              </Paper>
            </Box>
          </Box>
          
          {/* Metrics Row (responsive wrap on mobile, 5 columns on desktop) */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr', // stack on very small screens
                  sm: 'repeat(2, 1fr)', // two per row on small
                  md: 'repeat(3, 1fr)', // three per row on medium
                  lg: 'repeat(5, 1fr)', // five on large/desktop
                },
                gap: 1.5, // 12px
              }}
            >
              {[
                { label: 'Total Sales', value: <CurrencyFormatter value={orders.totalSales} /> },
                { label: 'Total Purchases', value: <CurrencyFormatter value={orders.totalPurchases} /> },
                { label: 'Total Customers', value: customers },
                { label: 'Total Suppliers', value: suppliers },
                { label: 'Total Orders', value: totalOrders },
              ].map((m) => (
                <Card
                  key={m.label}
                  className="surface-card"
                  sx={{
                    color: 'text.primary',
                    borderRadius: 2,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                    transition: 'transform .25s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ px: 1.5, py: 1.25, '&:last-child': { pb: 1.25 } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        opacity: 0.85
                      }}
                      gutterBottom
                    >
                      {m.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, lineHeight: 1.1 }}
                    >
                      {m.value}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Charts Section */}
          <Box>
            <Grid container spacing={2}>
              {/* Inventory Levels Pie Chart */}
              <Grid item xs={12} md={6}>
                <Paper className="surface-card" sx={{ p: 2, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>Inventory Levels</Typography>
                  <ResponsiveContainer width="100%" height={360}>
                    <PieChart>
                      <Pie
                        data={inventoryLevels}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        label
                      >
                        {inventoryLevels.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip type="inventory-levels" />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Inventory by Product (Scrollable Bar Chart replacing Pie) */}
              <Grid item xs={12} md={6}>
                <Paper className="surface-card" sx={{ p: 2, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>Inventory by Product</Typography>
                  <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <Box sx={{ minWidth: Math.max(inventoryData.length * 70, 600) }}>
                      <ResponsiveContainer width="100%" height={360}>
                        <BarChart
                          data={inventoryData}
                          margin={{ top: 20, right: 20, left: 0, bottom: 40 }}
                        >
                          <XAxis
                            dataKey="name"
                            interval={0}
                            tick={<ProductNameTick />}
                          />
                          <YAxis />
                          <Tooltip content={<CustomTooltip type="inventory" />} />
                          <Bar dataKey="value" fill="#3B82F6">
                            {inventoryData.map((entry, index) => (
                              <Cell key={`cell-prod-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Inventory Status Bar Chart */}
              <Grid item xs={12} md={6}>
                <Paper className="surface-card" sx={{ p: 2, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>Inventory Status</Typography>
                  <ResponsiveContainer width="100%" height={360}>
                    <BarChart data={inventoryLevels} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip content={<CustomTooltip type="inventory-levels" />} />
                      <Bar dataKey="value">
                        {inventoryLevels.map((entry, index) => (
                          <Cell key={`cell-status-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Orders Timeline Chart - Modified to be half width */}
              <Grid item xs={12} md={6}>
                <Paper className="surface-card" sx={{ p: 2, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>Orders Timeline</Typography>
                  <ResponsiveContainer width="100%" height={360}>
                    <BarChart 
                      data={orderHistoryAsc}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis 
                        dataKey="date"
                        tickFormatter={(date) => formatDateToStandard(date)}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip type="orders-timeline" />} />
                      <Legend />
                      <Bar dataKey="salesCount" name="Sales Orders" fill="#00C49F" stackId="a" />
                      <Bar dataKey="purchaseCount" name="Purchase Orders" fill="#FF8042" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Modified Inventory Movements Timeline Chart (date only + extra spacing) */}
              <Grid item xs={12}>
                <Paper className="surface-card" sx={{ p: 2, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>Inventory Movement by Product</Typography>
          <ResponsiveContainer width="100%" height={420}>
                    <LineChart
                      data={inventoryMovements}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <XAxis
                        dataKey="date_of_movement"
                        type="number"
                        scale="time"
                        domain={['auto', 'auto']}
                        tickFormatter={(timestamp) => {
                          const date = new Date(timestamp);
                          return formatDateToStandard(date);
                        }}
                        angle={-35}
                        textAnchor="end"
                        height={90}
                        tickMargin={16}
                      />
                      <YAxis
                        label={{
                          value: 'Quantity',
                          angle: -90,
                          position: 'insideLeft'
                        }}
                      />
                      <Tooltip
                        labelFormatter={(timestamp) => {
                          const date = new Date(timestamp);
                          return formatDateToStandard(date);
                        }}
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const date = new Date(label);
                            return (
                              <div className="chart-tooltip">
                                <p><strong>Date:</strong> {formatDateToStandard(date)}</p>
                                {payload.map((entry, index) => (
                                  <p key={index} style={{ color: entry.color }}>
                                    <strong>{entry.name}:</strong> {entry.value} units
                                  </p>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        align="center" 
                      />
                      {Array.isArray(groupedMovements) && groupedMovements.map((productName, index) => (
                        <Line
                          key={productName}
                          type="linear"
                          dataKey={productName}
                          name={productName}
                          stroke={COLORS[index % COLORS.length]}
                          dot={{ stroke: COLORS[index % COLORS.length], strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6 }}
                          strokeWidth={2}
                          connectNulls={true}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default Dashboard;
